import useNurses from "./useNurses";
import { useShiftsQuery } from "./roster-craete/useShiftsQuery";
import { useTeamsQuery } from "./roster-craete/useTeamsQuery";
import { useRosterLatestQuery } from "./roster-craete/useRosterLatestQuery";
import { useShiftManageQuery } from "./roster-craete/useShiftManageQuery";
import { useWantedListQuery } from "./roster-craete/useWantedListQuery";
import { groupNursesByPreceptor } from "@utils/nurse-util";
import { insertPreceptorData } from "@utils/preceptorUtils";
import { useMemo } from "react";
import useRosterConfig from "./useRosterConfig";

export function useRosterResources({ groupId }: { groupId: string }) {
  // 모든 쿼리를 병렬로 실행 (useRosterConfig 포함, 동일 queryKey로 캐시 공유)

  // 간호사
  const {
    data: nursesRes,
    isLoading: isNursesLoading,
    isError: isNursesError,
  } = useNurses(groupId);
  // shift
  const {
    data: shiftsRes,
    isLoading: isShiftsLoading,
    isError: isShiftsError,
  } = useShiftsQuery(groupId);
  // 팀리스트
  const {
    data: teamsRes,
    isLoading: isTeamsLoading,
    isError: isTeamsError,
  } = useTeamsQuery(groupId);
  // Config (useRosterConfig 기준으로 단일 소스 유지, 캐시 공유)
  const {
    data: configsRes,
    isLoading: isConfigsLoading,
    isError: isConfigsError,
  } = useRosterConfig();

  const shiftManageConfigVersion = useMemo(() => {
    const id = configsRes?.config_id;
    if (id != null && Number.isFinite(Number(id))) return String(id);
    return undefined;
  }, [configsRes?.config_id]);

  const {
    data: lastSchedule,
    isLoading: isLastScheduleLoading,
    isError: isLastScheduleError,
  } = useRosterLatestQuery(groupId);
  // 시프트 그룹핑 (현행 roster config의 config_id ↔ 백엔드 config_version 정합)
  const {
    data: shiftManager,
    isLoading: isShiftManagerLoading,
    isError: isShiftManagerError,
  } = useShiftManageQuery(groupId, shiftManageConfigVersion);
  // 원티드 리스트
  const {
    data: wantedList,
    isLoading: isWantedListLoading,
    isError: isWantedListError,
  } = useWantedListQuery(groupId);

  const fullNurseSchemaAddPreceptor = useMemo(() => {
    if (!nursesRes) return [];
    const nursesByPreceptor = groupNursesByPreceptor(nursesRes);

    return insertPreceptorData(nursesRes, nursesByPreceptor);
  }, [nursesRes]);

  const initalSchedule = useMemo(() => {
    if (!lastSchedule) return null;
    return {
      year: lastSchedule.year,
      month: lastSchedule.month,
      version: lastSchedule.version,
      scheduleId: lastSchedule.schedule_id,
    };
  }, [lastSchedule]);

  const isLoading =
    isNursesLoading ||
    isShiftsLoading ||
    isTeamsLoading ||
    isConfigsLoading ||
    isLastScheduleLoading ||
    isShiftManagerLoading ||
    isWantedListLoading;

  const isError =
    isNursesError ||
    isShiftsError ||
    isTeamsError ||
    isConfigsError ||
    isLastScheduleError ||
    isShiftManagerError ||
    isWantedListError;

  const result = useMemo(() => {
    if (isLoading) return { data: null, isLoading: true, isError: false };
    if (isError) return { data: null, isLoading: false, isError: true };

    return {
      data: {
        nurses: nursesRes,
        shifts: shiftsRes,
        teams: teamsRes,
        configs: configsRes,
        fullNurseSchemaAddPreceptor,
        lastSchedule,
        shiftManager,
        initalSchedule,
        wantedList,
      },
      isLoading: false,
      isError: false,
    };
  }, [
    isLoading,
    isError,
    nursesRes,
    shiftsRes,
    teamsRes,
    configsRes,
    fullNurseSchemaAddPreceptor,
    lastSchedule,
    shiftManager,
    initalSchedule,
    wantedList,
  ]);

  return result;
}
